import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FilterTextService } from '@services';
import { CommonLoadingComponent } from '@shared/common-loading/common-loading.component';
import { ColDef, ColumnApi, GridApi, GridOptions, GridReadyEvent, RowDragEndEvent } from 'ag-grid-community';
import { EventService } from 'app/core/services/event.service';
import { LayoutSettingsService } from 'app/core/services/layout-settings.service';
import { Subject, Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

/** Space kept below the grid for the pagination footer, when `useGridScroll` is on. */
const GRID_BOTTOM_GUTTER = 85;
/** Same, for a grid inside a modal, which also has the dialog's own footer below it. */
const MODAL_BOTTOM_GUTTER = 160;
/** Never shrink the grid below this, on very short windows. */
const MIN_GRID_HEIGHT = 300;
/** Body height to keep for the "no rows" overlay when the grid is empty. */
const EMPTY_GRID_BODY_HEIGHT = 150;

@Component({
  selector: 'app-datatable',
  templateUrl: './datatable.component.html',
  styleUrls: ['./datatable.component.scss'],
})
export class DatatableComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private gridApi!: GridApi;
  private columnApi!: ColumnApi;
  @Input() columnDefs: ColDef[] = [];
  @Output() onloadDataTable = new EventEmitter<any>();
  @Output() onPageSizeChangeTable = new EventEmitter<any>();
  @Output() onSortChangeTable = new EventEmitter<any>();
  @Output() onFilterChangeTable = new EventEmitter<any>();
  @Output() onPageChange = new EventEmitter<number>();
  @Output() onGlobalSearch = new EventEmitter<string>();
  @Output() onReset = new EventEmitter<null>();
  @Output() onDateRangeValueSelect = new EventEmitter<any>();
  @Output() exportList = new EventEmitter<string>();
  @Output() SelectedRowsData = new EventEmitter<any>();
  @Output() rowDragEnd = new EventEmitter<any>();

  @Input() rowData: any = [];

  @Input() listName: string = 'listName';
  @Input() perPage = 10;
  @Input() currentPage = 1;
  @Input() collectionSize = 1;
  @Input() filterText = '';
  @Input() sortField = 'id';
  @Input() sortOrder = 'asc';
  @Input() isPaginationEnable = true;
  @Input() resetFilters = true;
  @Input() isPrintEnable = true;
  @Input() isExportable = true;
  @Input() isDeletable = true;
  @Input() isSearchable = true;
  @Input() isDateRangeSelectionOn = false;
  @Input() paginationPosition: 'top' | 'bottom' | 'both' = 'bottom';
  @Input() rowSelection: 'single' | 'multiple' = 'multiple';
  @Input() rowGroupPanelShow: 'always' | 'onlyWhenGrouping' | 'never' = 'always';
  /**
   * Let AG Grid own its vertical scrolling (`domLayout: 'normal'` + a height on the grid element) instead of
   * `autoHeight` plus a max-height forced onto AG Grid's internal `.ag-body-viewport`. The forced max-height
   * creates a scroll container AG Grid does not know about: it sizes what it renders from `.ag-body-viewport`
   * at render time, so once that element is resized afterwards the newly exposed part is never drawn until
   * something triggers a redraw — the admin users table showed empty rows until the window was resized
   * (client, 25/10/2026). Ported from After School's datatable, where the same template had the same bug.
   */
  @Input() useGridScroll = true;
  @Input() defaultColDef: ColDef = {
    editable: false,
    enableRowGroup: false,
    enablePivot: false,
    enableValue: true,
    sortable: true,
    resizable: false,
    filter: true,
    flex: 1,
    pinned: false,
    lockPinned: true,
    minWidth: 50,
    valueFormatter: (params) => (params.value ? params.value : ' - '),
  };
  @Input() gridOptions: GridOptions = {
    rowHeight: 50,
    headerHeight: 40,
  };

  @Input() events!: Observable<void>;
  eventsSubscription!: Subscription;
  rounded = 1;
  pageSizeOptions: { pageSize: string; value: number | string }[] = [
    { pageSize: 'All', value: 'all' },
    { pageSize: '10', value: 10 },
    { pageSize: '15', value: 15 },
    { pageSize: '25', value: 25 },
    { pageSize: '50', value: 50 },
    { pageSize: '100', value: 100 },
  ];
  queryParamsSubject: Subject<string> = new Subject<string>();
  queryParams: string = '';
  dateRange: any;
  enableRtl: boolean = this._layoutService.getCurrentDirection() === 'rtl';
  showGrid: boolean = true;
  @Input() isLoading: boolean = false;
  @ViewChild('grid') block!: ElementRef;
  loadingOverlayComponent = CommonLoadingComponent;

  constructor(
    private _eventService: EventService,
    private _layoutService: LayoutSettingsService,
    private translateService: TranslateService,
    private filterTextService: FilterTextService,
    private elRef: ElementRef,
  ) {
    this.translateService.onLangChange.subscribe(() => {
      this.gridApi?.refreshHeader();
    });
  }
  /** The grid is sized to its own rows, so its height has to follow the row model. */
  onModelUpdated(): void {
    if (this.useGridScroll) {
      this.updateGridHeight();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.useGridScroll) {
      this.updateGridHeight();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setMaxHeight();
    this.pageSizeOptions[0].value = this.collectionSize;
  }
  ngAfterViewInit(): void {
    this.setMaxHeight();
  }
  ngOnInit(): void {
    this.setMaxHeight();

    this.eventsSubscription = this.events?.subscribe(() => this.gridApi.showLoadingOverlay());
    this.queryParamsSubject.pipe(debounceTime(500)).subscribe((val: string) => {
      // this.onGlobalSearch.emit(val);
      this.onGlobalSearch.emit(this.filterTextService.getFilterText(val));
    });

    let newPinnedValue: any;

    this._eventService.subscribe('changeDirection', (directionOfLayout: string) => {
      newPinnedValue = directionOfLayout === 'rtl' ? 'right' : 'left';
      const columnDefs = this.columnDefs;
      const columnToChange = columnDefs.find((col) => col.pinned === 'left' || col.pinned === 'right');
      this.setMaxHeight();
      if (columnToChange) {
        columnToChange.pinned = newPinnedValue;
        this.gridApi.setColumnDefs(columnDefs);
      }
      this.gridApi.redrawRows();
      this.showGrid = false;
      this.enableRtl = directionOfLayout === 'rtl';
      setTimeout(() => {
        this.showGrid = true;
      });
    });
  }

  ngOnDestroy() {
    this.eventsSubscription?.unsubscribe();
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
    if (localStorage.getItem(this.listName)) {
      this.columnApi.applyColumnState({
        state: JSON.parse(localStorage.getItem(this.listName) || '{}'),
        applyOrder: true,
      });
    }
    this.onloadDataTable.emit(params);
  }

  onPageSizeChanged() {
    this.pageSizeOptions[0].value = this.collectionSize;
    if ((this.perPage as any) === 'all' || this.perPage === this.collectionSize) {
      this.onPageSizeChangeTable.emit('all');
      // this.onPageSizeChangeTable.emit(); Important
    } else {
      this.onPageSizeChangeTable.emit(Number(this.perPage));
    }
    this.setMaxHeight();
  }

  onSortChange(event: any) {
    this.onSortChangeTable.emit(event);
  }

  onfilterChange(event: any) {
    const keys: string[] = []; //for language filtering
    const values: string[] = []; //for language filtering

    const filterFields = Object.entries(this.gridApi.getFilterModel()).map(([key, val]) => {
      const isFilteringNeeded =
        key === 'status' || key === 'sale' || key === 'hasBusinessCard' || key === 'adminApprovalStatus';
      if (isFilteringNeeded) {
        keys.push(key); //for language filtering
        values.push(val.filter.toString() ?? val.type.toString()); //for language filtering
      }
      return {
        [key]: {
          ...(isFilteringNeeded
            ? { $regex: this.filterTextService.getFilterText(val.filter.toString() ?? val.type.toString()) }
            : { $regex: val.filter.toString() ?? val.type.toString() }),
          $options: 'i',
        },
      };
    });
    this.onFilterChangeTable.emit({ filterFields, keys, values });
  }

  onPageClick(event: number) {
    this.onPageChange.emit(event);
  }

  onRowDragEnd(e: RowDragEndEvent) {
    const api = e.api;
    const rowDataArray: { _id: string; position: number }[] = [];
    const initialPosition = (this.currentPage - 1) * this.perPage + 1;
    let currentPosition = initialPosition;

    api?.forEachNode((node) => {
      const { _id } = node.data;
      rowDataArray.push({ _id, position: currentPosition });
      currentPosition++;
    });
    this.rowDragEnd.emit(rowDataArray);
  }

  onBtnExport() {
    // this.gridApi.exportDataAsCsv();
    this.exportList.emit('CSV');
  }

  onBtnPrint() {
    // FOR
    // const api = this.gridApi;
    // this.setPrinterFriendly(api);
    // setTimeout(() => {
    //   print();
    //   this.setNormal(api);
    // }, 2000);
    this.exportList.emit('PDF');
  }

  setPrinterFriendly(api: GridApi) {
    const eGridDiv = document.querySelector<HTMLElement>('#commonGrid')! as any;
    eGridDiv.style.height = '';
    api.setDomLayout('print');
  }

  setNormal(api: GridApi) {
    const eGridDiv = document.querySelector<HTMLElement>('#commonGrid')! as any;
    eGridDiv.style.height = '550px';
    api.setDomLayout();
  }

  onResetFilters() {
    // this.gridApi.showLoadingOverlay();
    // setTimeout(() => {
    //   this.gridApi.hideOverlay();
    // }, 3000);
    this.currentPage = 1;
    this.perPage = 10;
    this.queryParams = '';
    this.gridApi.setFilterModel(null);
    this.columnApi.resetColumnState();
    this.onReset.emit();
  }

  onColumnOrderChange() {
    if (this.columnApi) {
      const columnState = this.columnApi.getColumnState();
      const modifiedColumnState = columnState.map((column) => {
        const { pinned, ...newColumn } = column;
        return newColumn;
      });
      localStorage.setItem(this.listName, JSON.stringify(modifiedColumnState));
    }
  }

  setMaxHeight() {
    if (this.useGridScroll) {
      this.updateGridHeight();
      return;
    }
    if (this.block) {
      const datas = this.block.nativeElement.getBoundingClientRect();
      const bodyWrapper: HTMLElement | null = document.querySelector('.ag-body-viewport');
      if (bodyWrapper) {
        bodyWrapper.style.maxHeight =
          (window.innerHeight - datas.top > 73
            ? window.innerHeight - datas.top - 180 + (this.isPaginationEnable ? 40 : 100)
            : datas.top) + 'px';
        bodyWrapper.style.overflow = 'auto';
      }
    }
  }
  /**
   * Sizes the grid element itself, which is what `domLayout: 'normal'` expects: the space left on the page,
   * but never taller than its own rows (what `autoHeight` gave — small tables must still hug their content).
   * A paginated list is measured against the document, so scrolling the page does not change the height.
   */
  private updateGridHeight() {
    if (!this.block) {
      return;
    }
    const gridElement: HTMLElement | null = this.block.nativeElement.querySelector('ag-grid-angular');
    if (!gridElement) {
      return;
    }
    const rect = this.block.nativeElement.getBoundingClientRect();
    // a modal is position:fixed, so its top is already viewport-relative; stacked tables only get a screenful
    const inModal = !!this.elRef.nativeElement.closest('.modal-dialog');
    const spaceBelow = inModal
      ? window.innerHeight - rect.top - MODAL_BOTTOM_GUTTER
      : this.isPaginationEnable
        ? window.innerHeight - (rect.top + window.scrollY) - GRID_BOTTOM_GUTTER
        : window.innerHeight - GRID_BOTTOM_GUTTER;
    const available = Math.max(MIN_GRID_HEIGHT, spaceBelow);

    const header: HTMLElement | null = gridElement.querySelector('.ag-header');
    const horizontalScroll: HTMLElement | null = gridElement.querySelector('.ag-body-horizontal-scroll');
    const rowsContainer: HTMLElement | null = gridElement.querySelector('.ag-center-cols-container');
    // AG Grid parks an empty container at 1px, which is not a body worth showing
    const rowsHeight = rowsContainer ? parseFloat(rowsContainer.style.height) || 0 : 0;
    const content =
      (header?.offsetHeight ?? 0) +
      (horizontalScroll?.offsetHeight ?? 0) +
      (rowsHeight > 1 ? rowsHeight : EMPTY_GRID_BODY_HEIGHT) +
      2;

    const height = Math.round(Math.min(available, content)) + 'px';
    if (gridElement.style.height !== height) {
      gridElement.style.height = height;
    }
  }

  getSelectedRowsData() {
    const selectedNodes = this.gridApi.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data._id);
    this.SelectedRowsData.emit(selectedData);
  }
  onDateRangeSelect() {
    this.onDateRangeValueSelect.emit(this.dateRange);
  }
}
